/**
 * Centralized HITL (Human-In-The-Loop) State Management
 * 
 * Single source of truth for all HITL logic across client and extension.
 * 
 * Core Rules:
 * 1. HITL Request = message from "neo" with content_type "HUMAN_REVIEW" containing plans/tools/information_request
 * 2. HITL Response = message from user with content_type "HUMAN_REVIEW" 
 * 3. Regular Message = any message with content_type "MESSAGE" or any user message
 * 4. If last message is HITL Request → pending HITL
 * 5. If last message is HITL Response or Regular Message → no pending HITL
 */

export interface HITLState {
  hasPending: boolean;
  pendingMessageId: string | null;
  pendingMessage: any | null;
}

/**
 * Checks if a message has actual HITL request content
 * Looks for plans, tools, or information_request in various formats
 */
export function hasHITLContent(message: any): boolean {
  if (!message) return false;

  const hr = message.human_review;
  if (hr) {
    const hasInformationRequest = hr.information_request && Array.isArray(hr.information_request) && hr.information_request.length > 0;
    const hasPlans = hr.plans && Array.isArray(hr.plans) && hr.plans.length > 0;
    const hasTools = hr.tools && Array.isArray(hr.tools) && hr.tools.length > 0;
    if (hasInformationRequest || hasPlans || hasTools) return true;
  }

  if (typeof message.content === "string") {
    const trimmed = message.content.trim();
    let jsonStr = trimmed;
    
    if (trimmed.includes("Human Review") || trimmed.includes("human_review")) {
      const jsonMatch = trimmed.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        jsonStr = jsonMatch[0];
      }
    }
    
    if (jsonStr.startsWith("{") || jsonStr.startsWith("[")) {
      try {
        const json = JSON.parse(jsonStr);
        const hasPlans = json.plans && Array.isArray(json.plans) && json.plans.length > 0;
        const hasTools = json.tools && Array.isArray(json.tools) && json.tools.length > 0;
        const hasInformationRequest = (json.information_request || json.informationRequest) && 
                                      Array.isArray(json.information_request || json.informationRequest) && 
                                      (json.information_request || json.informationRequest).length > 0;
        if (hasPlans || hasTools || hasInformationRequest) return true;
      } catch {
        // Not valid JSON, ignore
      }
    }
  }

  return false;
}

/**
 * Checks if a message is a HITL request (from neo with HUMAN_REVIEW content)
 */
export function isHITLRequest(message: any): boolean {
  if (!message) return false;
  if (message.user_id !== "neo") return false;
  if (message.content_type !== "HUMAN_REVIEW") return false;
  return hasHITLContent(message);
}

/**
 * Checks if a message is a HITL response (from user with HUMAN_REVIEW content)
 */
export function isHITLResponse(message: any): boolean {
  if (!message) return false;
  if (message.user_id === "neo") return false;
  if (message.content_type !== "HUMAN_REVIEW") return false;
  return true;
}

/**
 * Checks if a message is a regular message (not HITL)
 */
export function isRegularMessage(message: any): boolean {
  if (!message) return false;
  // MESSAGE type from anyone
  if (message.content_type === "MESSAGE") return true;
  // User messages (not from neo) are always regular
  if (message.user_id !== "neo") return true;
  return false;
}

/**
 * Determines HITL state from messages and Redux state.
 * Only checks the last message - chat is disabled only if last message is a HITL request.
 */
export function getHITLState(
  messages: any[],
  explicitHitlState?: HITLState
): HITLState {
  if (messages && messages.length > 0) {
    const lastMessage = messages[messages.length - 1];

    if (isHITLResponse(lastMessage)) {
      return {
        hasPending: false,
        pendingMessageId: null,
        pendingMessage: null,
      };
    }

    if (lastMessage.user_id === "neo" && lastMessage.content_type === "MESSAGE") {
      return {
        hasPending: false,
        pendingMessageId: null,
        pendingMessage: null,
      };
    }

    if (isHITLRequest(lastMessage)) {
      if (
        lastMessage.human_review_status === "approved" ||
        lastMessage.human_review_status === "canceled" ||
        lastMessage.human_review_status === "scheduled"
      ) {
        return {
          hasPending: false,
          pendingMessageId: null,
          pendingMessage: null,
        };
      }
      return {
        hasPending: true,
        pendingMessageId: lastMessage.message_id || null,
        pendingMessage: lastMessage,
      };
    }
  }

  if (explicitHitlState !== undefined) {
    return explicitHitlState;
  }

  return {
    hasPending: false,
    pendingMessageId: null,
    pendingMessage: null,
  };
}

/** CONTINUE and UPDATE are both approvals; UPDATE means arguments also changed. */
export function isHitlApprovalAction(action: unknown): boolean {
  const normalized = String(action || "").trim().toUpperCase();
  return normalized === "CONTINUE" || normalized === "UPDATE";
}
